import React from "react";
import { ArrowUpRight, Instagram, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteData } from "../context/SiteDataContext";
import { getSection, renderMultilineText } from "../lib/sectionStyles";

const Footer = () => {
  const { writer, siteSections } = useSiteData();
  const section = getSection(siteSections, "global", "global.footer", {
    title: "MAI EL\nMOKADEM.",
    body: "Writing the region into the room before someone else writes it out.",
    label: `The Writing Portfolio - ${writer.issue}`,
    caption: "Printed in Cairo - Distributed Worldwide",
  });

  if (!section) return null;

  const titleLines = String(section.title || writer.name).split(/\n+/).filter(Boolean);

  return (
    <footer className="mt-24 bg-[var(--ink)] text-[var(--paper)]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="flex items-center gap-2 mb-4">
              <span className="h-2 w-8 bg-[var(--pink)]" />
              <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--pink-soft)]/80 uppercase">
                {section.label}
              </p>
            </div>
            <h3 className="font-masthead text-[14vw] md:text-[9vw] leading-[0.85]">
              {titleLines[0] || "MAI EL"}
              <br />
              {titleLines[1]?.replace(/\.$/, "") || "MOKADEM"}
              <span className="text-[var(--pink)]">.</span>
            </h3>
            <p className="font-editorial italic text-2xl md:text-3xl mt-6 max-w-xl text-[var(--pink-soft)]">
              "{renderMultilineText(section.body)}"
            </p>
          </div>

          <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--pink-soft)]/60 uppercase mb-4">
                Contact
              </p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Mail size={14} />
                  <a href={`mailto:${writer.email}`} className="uline break-all">
                    {writer.email}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Instagram size={14} />
                  <a href={writer.instagramUrl} target="_blank" rel="noopener noreferrer" className="uline">
                    @{writer.instagram}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--pink-soft)]/60 uppercase mb-4">
                Pages
              </p>
              <ul className="space-y-3 text-sm font-masthead tracking-[0.2em]">
                <li>
                  <Link to="/" className="uline">HOME</Link>
                </li>
                <li>
                  <Link to="/about" className="uline">ABOUT</Link>
                </li>
                <li>
                  <a href={`mailto:${writer.email}`} className="uline inline-flex items-center gap-1">
                    PITCH ME <ArrowUpRight size={14} />
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--pink-soft)]/60 uppercase mb-4">
                Writing Beats
              </p>
              <ul className="space-y-3 text-sm">
                <li><Link to="/journalist-portfolio" className="uline">Journalist Portfolio</Link></li>
                <li><Link to="/fashion-journalist" className="uline">Fashion Journalist</Link></li>
                <li><Link to="/music-journalist" className="uline">Music Journalist</Link></li>
                <li><Link to="/cultural-commentary" className="uline">Cultural Commentary</Link></li>
                <li><Link to="/interviews" className="uline">Interviews</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[var(--paper)]/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] font-mono uppercase tracking-[0.25em] text-[var(--pink-soft)]/60">
          <span>(c) {new Date().getFullYear()} Mai El Mokadem</span>
          <span>{section.caption}</span>
          <span>No.{writer.issue.split("No.")[1] || "26"} / {writer.year}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
